import React from 'react';
import { useEffect, useState } from 'react';

import {
  APITruckGpsModel,
  APITruckOperationalStatus,
} from '@cd3p/core/types/api';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  GlobalLoader,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from 'components';
import { useHandleApiResult } from 'hooks/useHandleApiResult';

import { styled, useSelector, useTranslation } from 'third-party';

import { useTracking } from 'modules/tracking';

import { trackingSelectors } from 'selectors';

const Container = styled(Stack)`
  flex-direction: column;
  width: 80rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  min-height: 0;
  flex-grow: 1;
`;

const StyledList = styled(List)`
  border: 1px solid ${props => props.theme.custom.palette.muted100};
  border-radius: 0.4rem;
  width: 100%;
  height: 40rem;
  overflow: auto;
  padding: 0;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  box-shadow: none;
  padding: 2.2rem 2.3rem 1.8rem;
  z-index: 1;
  border-bottom: 1px solid ${props => props.theme.custom.palette.muted100};
`;

const Title = styled(Typography)`
  line-height: 100%;
  color: ${props => props.theme.custom.palette.primary900};
  margin-bottom: 0;
  font-size: 1.8rem;
  font-weight: 900;
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1.6rem;
`;

const StyledButton = styled(Button)`
  font-size: 1.2rem;
  font-weight: 700;
  border-width: 0.2rem;
  &:hover {
    border-width: 0.2rem;
  }
`;

const StyledErrorText = styled(Typography)`
  width: 100%;
  height: 40rem;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.8rem;
  font-weight: 900;
  color: ${props => props.theme.custom.palette.primary900};
`;

const StickyListItem = styled(ListItem)`
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.palette.background.paper};
  z-index: 2;
  border-bottom: 1px solid ${({ theme }) => theme.custom.palette.muted100};
`;

const NewItemBadge = styled(Chip)`
  margin-left: 0.8rem;
  font-size: 1.1rem;
  font-weight: bold;
  height: 2.2rem;
  background-color: ${({ theme }) => theme.custom.palette.primary700};
  color: white;
`;

const Description = styled(Typography)`
  font-size: 1.4rem;
  margin: 0;
  line-height: 1.5;
  width: 100%;
  padding: 1.5rem 2.3rem;
  box-sizing: border-box;
`;

type GpsTrucksListProps = {
  providerId: string;
  onClose: () => void;
};

export const GpsTrucksList: React.FC<GpsTrucksListProps> = ({
  providerId,
  onClose,
}) => {
  const { t } = useTranslation();
  const handleApiResult = useHandleApiResult();
  const { getGpsTrucks, syncTrucks, resetGpsTrucks } = useTracking();
  const [gpsTrucks, setGpsTrucks] = useState<APITruckGpsModel[]>([]);
  const [selectedTrucks, setSelectedTrucks] = useState<APITruckGpsModel[]>([]);

  const getGpsTrucksPending = useSelector(
    trackingSelectors.getGpsTrucksPending,
  );
  const getGpsTrucksError = useSelector(trackingSelectors.getGpsTrucksError);
  const syncTrucksPending = useSelector(trackingSelectors.syncTrucksPending);

  useEffect(() => {
    handleApiResult<APITruckGpsModel[]>(
      () => getGpsTrucks(providerId),
      ({ result }) => {
        setGpsTrucks(result.payload);
      },
      ({ showErrorToast }) => {
        showErrorToast(t('settings.integrations.form.testConnectionError'));
      },
    );

    return () => {
      resetGpsTrucks();
      setGpsTrucks([]);
    };
  }, [getGpsTrucks, providerId, resetGpsTrucks, t, handleApiResult]);

  useEffect(() => {
    setSelectedTrucks(
      gpsTrucks.filter(
        truck =>
          truck.syncedTruckId !== null &&
          truck.truckOperationalStatus !== APITruckOperationalStatus.Inactive,
      ),
    );
  }, [gpsTrucks]);

  const handleSelectTruck = (truck: APITruckGpsModel) => {
    setSelectedTrucks(prev => [...prev, truck]);
  };

  const handleDeselectTruck = (truck: APITruckGpsModel) => {
    setSelectedTrucks(prev => prev.filter(t => t.deviceId !== truck.deviceId));
  };

  const handleSyncTrucks = async () => {
    await handleApiResult(
      () =>
        syncTrucks(
          providerId,
          selectedTrucks.map(truck => truck.deviceId || ''),
        ),
      ({ showBaseToast }) => {
        showBaseToast(t('settings.integrations.form.syncTrucksSuccess'));
        onClose();
      },
      ({ showErrorToast }) => {
        showErrorToast(t('settings.integrations.form.syncTrucksError'));
      },
    );
  };

  const handleSelectAllTrucks = (checked: boolean) => {
    setSelectedTrucks(checked ? gpsTrucks : []);
  };

  return (
    <Container>
      <Header>
        <Title variant="h3">{t('settings.integrations.form.syncTrucks')}</Title>
        <ActionButtons>
          <StyledButton
            variant="outlined"
            color="primary"
            onClick={() => onClose()}
          >
            {t('common.cancel')}
          </StyledButton>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleSyncTrucks()}
            disabled={syncTrucksPending}
            loading={syncTrucksPending}
          >
            {t('settings.integrations.form.syncTrucks')}
          </Button>
        </ActionButtons>
      </Header>
      <Description variant="body2">
        {t('settings.integrations.form.syncTrucksDescription')}
      </Description>
      {getGpsTrucksPending ? (
        <GlobalLoader />
      ) : getGpsTrucksError ? (
        <StyledErrorText variant="h6">
          {t('settings.integrations.form.unableToLoadGpsTrucks')}
        </StyledErrorText>
      ) : (
        <StyledList>
          <StickyListItem
            secondaryAction={
              <Checkbox
                checked={selectedTrucks.length === gpsTrucks.length}
                onChange={e => handleSelectAllTrucks(e.target.checked)}
              />
            }
          >
            <ListItemText
              key="select-all"
              primary={
                <Typography fontWeight="bold" color="primary">
                  {t('common.selectAll')}
                </Typography>
              }
            />
          </StickyListItem>
          {gpsTrucks.map(truck => (
            <ListItem
              key={truck.deviceId}
              secondaryAction={
                <Checkbox
                  checked={selectedTrucks.includes(truck)}
                  onChange={() =>
                    selectedTrucks.includes(truck)
                      ? handleDeselectTruck(truck)
                      : handleSelectTruck(truck)
                  }
                />
              }
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <span>{`${t('settings.integrations.form.truckNumber')}: ${
                      truck.truckNumber
                    }`}</span>
                    {truck.truckOperationalStatus === 'Inactive' && (
                      <NewItemBadge size="small" label={t('common.new')} />
                    )}
                  </Box>
                }
                secondary={`${t('settings.integrations.form.deviceId')}: ${
                  truck.deviceId
                }`}
              />
            </ListItem>
          ))}
        </StyledList>
      )}
    </Container>
  );
};
